'use client';

import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const STREAM_URL = 'https://bihnet.net/livecam/index.m3u8';
const POSTER_URL = '/images/livecam-poster.jpg';

export default function LiveCamPlayer() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0); // forsira recreate

  /* ================= HELPERS ================= */

  const clearErrorTimer = () => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  };

  const scheduleError = (delay = 5000) => {
    clearErrorTimer();
    errorTimerRef.current = setTimeout(() => {
      setHasError(true);
      setIsLoading(false);
    }, delay);
  };

  const markHealthy = () => {
    clearErrorTimer();
    setHasError(false);
    setIsLoading(false);
  };

  /* ============== PLAYER CREATION ============== */

  const createPlayer = () => {
    const video = videoRef.current;
    if (!video) return;

    // reset HTML5 video
    video.pause();
    video.removeAttribute('src');
    video.load();

    setIsLoading(true);
    setHasError(false);

    // SAFARI / iOS (native HLS)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = STREAM_URL;

      video.onloadedmetadata = () => {
        markHealthy();
        video.play().catch(() => {});
      };

      video.onerror = () => scheduleError();
      return;
    }

    // HLS.js (Chrome, Firefox, Edge)
    if (Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: true,
        fragLoadingRetryDelay: 1500,
        fragLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 1500,
        manifestLoadingMaxRetry: 6,
        maxLiveSyncPlaybackRate: 1.5,
      });

      hlsRef.current = hls;
      hls.loadSource(STREAM_URL);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        markHealthy();
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.FRAG_LOADED, markHealthy);

      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;

        scheduleError();

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;

          default:
            break;
        }
      });
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  };

  /* ============== FULL RECREATE (BUTTON) ============== */

  const recreatePlayer = () => {
    // hard reset
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    clearErrorTimer();
    setRetryKey((k) => k + 1);
  };

  /* ============== EFFECTS ============== */

  useEffect(() => {
    createPlayer();

    const onVisibility = () => {
      if (!document.hidden) {
        recreatePlayer();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearErrorTimer();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  /* ================= UI ================= */

  return (
    <section data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:153:4" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="153" data-component-file="LiveCamPlayer.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22font-sans%20w-full%20bg-white%20py-14%22%7D" className="font-sans w-full bg-white py-14">
      <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:154:6" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="154" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20mx-auto%20px-4%22%7D" className="max-w-6xl mx-auto px-4">

        {/* HEADER */}
        <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:157:8" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="157" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20sm%3Aitems-end%20sm%3Ajustify-between%20mb-6%22%7D" className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6">
          <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:158:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="158" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h2 data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:159:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="159" data-component-file="LiveCamPlayer.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20sm%3Atext-4xl%20font-extrabold%20text-gray-900%22%2C%22textContent%22%3A%22U%C5%BDIVO%20%E2%80%93%20Grada%C4%8Dac%20Kamera%22%7D" className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              UŽIVO – Gradačac Kamera
            </h2>
            <p data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:162:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="162" data-component-file="LiveCamPlayer.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-gray-500%20mt-2%22%2C%22textContent%22%3A%22Direktan%20HD%20prenos%20preko%20Telesky%20infrastrukture.%22%7D" className="text-sm text-gray-500 mt-2">
              Direktan HD prenos preko Telesky infrastrukture.
            </p>
          </div>

          <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:167:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="167" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mt-3%20sm%3Amt-0%20inline-flex%20items-center%20gap-2%20bg-red-500%2F10%20border%20border-red-400%2F40%20px-4%20py-1.5%20rounded-xl%22%7D" className="mt-3 sm:mt-0 inline-flex items-center gap-2 bg-red-500/10 border border-red-400/40 px-4 py-1.5 rounded-xl">
            <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:168:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="168" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22relative%20flex%20h-3%20w-3%22%7D" className="relative flex h-3 w-3">
              <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:169:14" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="169" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20inline-flex%20h-full%20w-full%20animate-ping%20rounded-full%20bg-red-500%20opacity-75%22%7D" className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:170:14" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="170" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22relative%20inline-flex%20h-3%20w-3%20rounded-full%20bg-red-600%22%7D" className="relative inline-flex h-3 w-3 rounded-full bg-red-600" />
            </span>
            <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:172:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="172" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-red-600%20text-sm%22%2C%22textContent%22%3A%22LIVE%22%7D" className="font-semibold text-red-600 text-sm">LIVE</span>
          </span>
        </div>

        {/* PLAYER */}
        <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:177:8" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="177" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20rounded-3xl%20shadow-xl%20overflow-hidden%20border%20border-gray-200%20bg-black%22%7D" className="relative w-full rounded-3xl shadow-xl overflow-hidden border border-gray-200 bg-black">
          <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:178:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="178" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20aspect-video%22%7D" className="relative w-full aspect-video">
            <video data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:179:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="179" data-component-file="LiveCamPlayer.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%20object-cover%22%7D"
              ref={videoRef}
              key={retryKey}
              className="absolute inset-0 w-full h-full object-cover"
              controls
              playsInline
              muted
              poster={POSTER_URL}
            />
          </div>

          {/* LOADING */}
          {isLoading && !hasError && (
            <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:192:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="192" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20bg-white%2F40%20backdrop-blur-md%22%7D" className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-md">
              <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:193:14" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="193" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%22%7D" className="flex flex-col items-center">
                <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:194:16" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="194" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20h-10%20w-10%20border-t-2%20border-b-2%20border-emerald-500%20rounded-full%22%7D" className="animate-spin h-10 w-10 border-t-2 border-b-2 border-emerald-500 rounded-full" />
                <p data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:195:16" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="195" data-component-file="LiveCamPlayer.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20text-emerald-600%20text-sm%22%2C%22textContent%22%3A%22Povezivanje%20na%20live%20stream%E2%80%A6%22%7D" className="mt-4 text-emerald-600 text-sm">
                  Povezivanje na live stream…
                </p>
              </div>
            </div>
          )}

          {/* ERROR */}
          {hasError && (
            <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:204:12" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="204" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20bg-white%2F70%20backdrop-blur-lg%20px-4%20text-center%22%7D" className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-lg px-4 text-center">
              <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:205:14" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="205" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <p data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:206:16" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="206" data-component-file="LiveCamPlayer.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-red-600%20font-semibold%20text-lg%22%2C%22textContent%22%3A%22Stream%20trenutno%20nije%20dostupan%22%7D" className="text-red-600 font-semibold text-lg">
                  Stream trenutno nije dostupan
                </p>
                <p data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:209:16" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="209" data-component-file="LiveCamPlayer.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20text-sm%20mt-2%22%2C%22textContent%22%3A%22Poku%C5%A1avamo%20ponovo%20uspostaviti%20vezu.%22%7D" className="text-gray-600 text-sm mt-2">
                  Pokušavamo ponovo uspostaviti vezu.
                </p>
                <button data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:212:16" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="212" data-component-file="LiveCamPlayer.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22mt-4%20px-5%20py-2%20rounded-lg%20bg-emerald-600%20text-white%20text-sm%20hover%3Abg-emerald-700%22%2C%22textContent%22%3A%22Poku%C5%A1aj%20ponovo%22%7D"
                  onClick={recreatePlayer}
                  className="mt-4 px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700"
                >
                  Pokušaj ponovo
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:224:8" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="224" data-component-file="LiveCamPlayer.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%20justify-center%20gap-4%20text-gray-600%20text-sm%20mt-6%22%2C%22textContent%22%3A%22%E2%80%A2%20%E2%80%A2%20%E2%80%A2%22%7D" className="flex flex-wrap items-center justify-center gap-4 text-gray-600 text-sm mt-6">
          <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:225:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="225" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Full%20HD%201080p%22%7D">Full HD 1080p</span> •
          <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:226:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="226" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Niska%20latencija%22%7D">Niska latencija</span> •
          <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:227:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="227" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Telesky%20Stream%20Gateway%22%7D">Telesky Stream Gateway</span> •
          <span data-component-id="src/app/homepage/components/LiveCamPlayer.tsx:228:10" data-component-path="src/app/homepage/components/LiveCamPlayer.tsx" data-component-line="228" data-component-file="LiveCamPlayer.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%2224%2F7%20Live%22%7D">24/7 Live</span>
        </div>
      </div>
    </section>
  );
}
